import { useRef, useState } from "react";
import { Loader2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { errMsg, imgUrl, label, uploadImage } from "@/lib/api";

const TONES = {
  green: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
  amber: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
  red: "bg-rose-500/10 text-rose-700 dark:text-rose-400 border-rose-500/20",
  blue: "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20",
  purple: "bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20",
  gray: "bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20",
  indigo: "bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border-indigo-500/20",
};

const STATUS_TONE = {
  NEW_INQUIRY: "blue", CONTACTED: "blue", REQUIREMENT_GATHERED: "purple", QUOTATION_SENT: "indigo", NEGOTIATION: "amber",
  WON_APPROVED: "green", LOST: "gray", DRAFT: "gray", SENT_TO_CLIENT: "indigo", APPROVED_BY_CLIENT: "green", SUPERSEDED: "gray",
  ACTIVE: "blue", ON_HOLD: "amber", COMPLETED: "green", CANCELLED: "gray", OPEN: "blue", IN_PROGRESS: "amber",
  WAITING_ON_CLIENT: "purple", RESOLVED: "green", CLOSED: "gray", PAID: "green", PENDING: "amber", REFUNDED: "gray",
  FULFILLED: "green", UNFULFILLED: "amber", PARTIAL: "purple", SENT_TO_VENDOR: "purple", REPLACEMENT_RECEIVED: "green",
  REJECTED: "red", HIGH: "red", URGENT: "red", MEDIUM: "amber", LOW: "gray",
};

export function Tone({ tone = "gray", children, className = "", ...rest }) {
  return (
    <Badge variant="outline" className={`${TONES[tone]} font-medium rounded-md px-2 py-0.5 text-[11px] whitespace-nowrap ${className}`} {...rest}>
      {children}
    </Badge>
  );
}

export function StatusBadge({ status, ...rest }) {
  return <Tone tone={STATUS_TONE[status] || "gray"} {...rest}>{label(status)}</Tone>;
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8 fade-up">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground mt-1.5">{subtitle}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

export function Panel({ title, action, children, className = "", ...rest }) {
  return (
    <div className={`rounded-lg border bg-card ${className}`} {...rest}>
      {title && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-3.5 border-b">
          <h3 className="text-sm font-semibold">{title}</h3>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

export function Empty({ text }) {
  return <div className="py-14 text-center text-sm text-muted-foreground">{text}</div>;
}

export function ImageUploader({ images, onChange, testid = "image-upload", disabled }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onFiles = async (files) => {
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) paths.push(await uploadImage(f));
      onChange([...(images || []), ...paths]);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
      ref.current.value = "";
    }
  };
  return (
    <div className="flex flex-wrap gap-3">
      {(images || []).map((p, i) => (
        <div key={p + i} className="relative h-20 w-20 rounded-md overflow-hidden border group">
          <img src={imgUrl(p)} alt="" className="h-full w-full object-cover" />
          {!disabled && (
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))}
              className="absolute top-1 right-1 rounded bg-black/60 p-0.5 text-white opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testid}-remove-${i}`}>
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      ))}
      {!disabled && (
        <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testid}-button`}
          className="h-20 w-20 rounded-md border border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors text-[11px] gap-1">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
          {busy ? "Uploading" : "Upload"}
        </button>
      )}
      <input ref={ref} type="file" accept="image/*" multiple hidden onChange={(e) => onFiles([...e.target.files])} data-testid={`${testid}-input`} />
    </div>
  );
}
